import * as React from "react";
import { MapPin } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import { MediaThumb } from "@/v2/components/MediaThumb";
import {
  CONTENT_TYPE_LABEL,
  formatContentDate,
  formatTimecode,
  setTranslucentDragImage,
} from "../schedulerUtils";
import type { ContentItem, DragPayload, ProgramColor } from "../types";
import type { TouchDragHandle } from "../useTouchDragBridge";

export { MediaThumb };

export function colorStyle(color: ProgramColor): React.CSSProperties {
  return {
    background: `var(--v2-sched-${color}-bg)`,
    borderColor: `var(--v2-sched-${color}-border)`,
  };
}

/**
 * Bordered shell for inputs/selects. In .v2 a border utility on the control
 * itself draws nothing (see CLAUDE.md), so the border lives on this wrapper
 * and focus is shown with focus-within.
 */
export function Field({
  className,
  style,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-center rounded-md border border-input bg-card focus-within:border-action focus-within:ring-[3px] focus-within:ring-action/25",
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

/** Classes that strip a control's own chrome so it can sit inside a Field. */
export const BARE_CONTROL =
  "sched-bare h-9 border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0";

export function ContentCard({
  item,
  dragRef,
  touchDrag,
  onSelect,
}: {
  item: ContentItem;
  /** When given the card is a drag source for the scheduler. */
  dragRef?: React.MutableRefObject<DragPayload | null>;
  /** Bridges touch input onto the same drag (see useTouchDragBridge); has no
   *  effect without dragRef. */
  touchDrag?: TouchDragHandle;
  onSelect?: (item: ContentItem) => void;
}) {
  const draggable = Boolean(dragRef);
  const Wrapper = onSelect ? "button" : "div";
  return (
    <Wrapper
      type={onSelect ? "button" : undefined}
      draggable={draggable}
      data-testid={`content-card-${item.id}`}
      onClick={onSelect ? () => onSelect(item) : undefined}
      onDragStart={
        dragRef
          ? (e: React.DragEvent) => {
              dragRef.current = {
                kind: "content",
                id: item.id,
                durationMin: Math.ceil(item.durationSec / 60),
                grabOffsetMin: 0,
                channelId: item.channelId,
              };
              e.dataTransfer.effectAllowed = "copy";
              e.dataTransfer.setData("text/plain", item.id);
              setTranslucentDragImage(e, e.currentTarget as HTMLElement);
            }
          : undefined
      }
      onDragEnd={dragRef ? () => (dragRef.current = null) : undefined}
      {...(dragRef
        ? touchDrag?.bind(
            () => ({
              kind: "content",
              id: item.id,
              durationMin: Math.ceil(item.durationSec / 60),
              grabOffsetMin: 0,
              channelId: item.channelId,
            }),
            item.title,
          )
        : undefined)}
      className={cn(
        "block w-full rounded-md bg-card text-left",
        draggable && "cursor-grab active:cursor-grabbing",
        onSelect && "hover:bg-accent",
      )}
    >
      <div
        className="relative overflow-hidden rounded-md"
        style={{ aspectRatio: "16 / 9" }}
      >
        <MediaThumb
          thumbnail={item.thumbnail}
          logos={item.logos}
          className="h-full w-full"
        />
        {item.channelLogo ? (
          <img
            src={item.channelLogo}
            alt=""
            className="absolute rounded bg-white/90 object-contain"
            style={{ left: 6, top: 6, width: 36, height: 24, padding: 2 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.visibility = "hidden";
            }}
          />
        ) : null}
        {item.durationEstimated ? null : (
          <span
            className="absolute rounded bg-black/70 text-white"
            style={{
              right: 6,
              bottom: 6,
              padding: "2px 6px",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {formatTimecode(item.durationSec)}
          </span>
        )}
      </div>
      <div style={{ padding: "6px 2px 2px" }}>
        <div className="line-clamp-2 text-[13px] font-medium leading-tight text-foreground">
          {item.title}
        </div>
        <div className="text-xs text-muted-foreground" style={{ marginTop: 4 }}>
          {formatContentDate(item.date)}
          {item.time ? ` · ${item.time}` : ""}
        </div>
        {item.facility ? (
          <div
            className="flex items-center gap-1 text-xs text-muted-foreground"
            style={{ marginTop: 2 }}
          >
            <MapPin className="size-3 shrink-0" />
            <span className="truncate">{item.facility}</span>
          </div>
        ) : null}
      </div>
    </Wrapper>
  );
}
